import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import { spacing } from "../../theme";
import { useColors, ThemeColors } from "../../hooks/useColors";
import { useOrders, Order } from "../../hooks/useOrders";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../../types/navigation";
import { format } from "date-fns";
import { BuyerOrder } from "../../screens/BuyerOrderDetailsScreen";
import { orderEvents } from "../../utils/orderEvents";
import { supabase } from "../../lib/supabase";
import { InAppBrowserModal } from "../ui/InAppBrowserModal";
import { logger } from "../../utils/logger";
import { registerCacheClearFunction } from "../../utils/authCacheClear";

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

// Module-level cache to persist across component unmounts/remounts
// This prevents flash when navigating between screens
let cachedOngoingOrders: Order[] = [];
let hasEverShownContent = false;
// Track which user the cache belongs to - clear cache when user changes
let cachedForUserId: string | null = null;

// Clear cache function - called on auth state changes to prevent cross-user data leakage
function clearBuyerOrderCache(): void {
  console.log('[OngoingOrderDisplay] Clearing module-level cache');
  cachedOngoingOrders = [];
  hasEverShownContent = false;
  cachedForUserId = null;
}

// Register with central auth cache clearing system
registerCacheClearFunction(clearBuyerOrderCache);

const getStatusColor = (status: string) => {
  const jadeGreen = "#00A86B";
  const roseRed = "#F43F5E";
  switch (status.toLowerCase()) {
    case "pending":
      return "#F59E0B"; // warning/amber
    case "confirmed":
    case "paid":
    case "succeeded":
    case "wrapping_up":
    case "packed":
    case "pickup_pending":
    case "pickup_complete":
    case "in_transit":
    case "delivered":
    case "completed":
      return jadeGreen;
    case "cancelled":
    case "rejected":
    case "failed":
      return roseRed;
    default:
      return "#6B7280"; // gray
  }
};

const getStatusText = (status: string) => {
  switch (status.toLowerCase()) {
    case "pending":
      return "Pending Approval";
    case "confirmed":
      return "Order Confirmed";
    case "paid":
    case "succeeded":
      return "Waiting for Seller";
    case "wrapping_up":
      return "Wrapping Up";
    case "packed":
      return "Packed";
    case "pickup_pending":
      return "Courier on the Way";
    case "pickup_complete":
      return "Pickup Complete";
    case "in_transit":
      return "In Transit";
    case "delivered":
      return "Delivered";
    case "completed":
      return "Completed";
    case "cancelled":
      return "Cancelled";
    case "rejected":
      return "Rejected";
    case "failed":
      return "Failed";
    default:
      // Convert snake_case to Title Case (e.g., "pickup_pending" -> "Pickup Pending")
      return status
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
  }
};

const getStatusIcon = (status: string) => {
  switch (status.toLowerCase()) {
    case "pending":
      return "clock"; // Feather icon
    case "confirmed":
    case "paid":
    case "succeeded":
      return "check-circle";
    case "wrapping_up":
      return "package";
    case "packed":
      return "package"; // Using package for packed items
    case "pickup_pending":
      return "truck"; // Using truck as closest Feather alternative
    case "pickup_complete":
      return "check-circle";
    case "in_transit":
      return "truck";
    case "delivered":
    case "completed":
      return "check-circle";
    case "cancelled":
    case "rejected":
    case "failed":
      return "x-circle";
    default:
      return "circle";
  }
};

const isOngoingOrder = (status: string): boolean => {
  // Use the same logic as OrderTrackingBanner - filter out completed, delivered, cancelled, or rejected
  return status && !["completed", "delivered", "cancelled", "rejected"].includes(status);
};

interface OngoingOrderCardProps {
  order: Order;
  onPress: () => void;
  onTrack: () => void;
  colors: ThemeColors;
  styles: any;
}

const OngoingOrderCard: React.FC<OngoingOrderCardProps> = ({
  order,
  onPress,
  onTrack,
  colors,
  styles,
}) => {
  const statusColor = getStatusColor(order.status);
  const statusText = getStatusText(order.status);
  const statusIcon = getStatusIcon(order.status);

  // Get first few items to display
  const displayItems = order.items.slice(0, 2);
  const remainingCount = order.items.length - displayItems.length;
  
  // Check if order can be tracked (has tracking_url and is in delivery phase)
  const canTrack = order.tracking_url && ["pickup_pending", "pickup_complete", "in_transit"].includes(order.status.toLowerCase());

  return (
    <TouchableOpacity style={styles.orderCard} onPress={onPress}>
      <View style={styles.cardHeader}>
        <View style={styles.orderInfo}>
          <Text style={styles.orderId}>
            Order #{order.id.slice(-6).toUpperCase()}
          </Text>
          <Text style={styles.orderDate}>
            {format(new Date(order.created_at), "MMM dd, h:mm a")}
          </Text>
        </View>
        <View style={[styles.statusBadge, { backgroundColor: statusColor }]}>
          <Feather name={statusIcon as any} size={12} color="#000000" />
          <Text style={styles.statusText}>{statusText}</Text>
        </View>
      </View>

      <View style={styles.itemsContainer}>
        {displayItems.map((item, index) => (
          <Text key={index} style={styles.itemText}>
            {item.quantity}x {item.name}
          </Text>
        ))}
        {remainingCount > 0 && (
          <Text style={styles.moreItemsText}>
            +{remainingCount} more item{remainingCount > 1 ? "s" : ""}
          </Text>
        )}
      </View>

      <View style={styles.cardFooter}>
        <Text style={styles.totalAmount}>${order.total_amount}</Text>
        <View style={styles.actionContainer}>
          <Text style={styles.viewDetailsText}>View Details</Text>
          <Feather name="chevron-right" size={16} color={colors.textLight} />
        </View>
      </View>
      
      {/* Track Order button for buyers */}
      {canTrack && (
        <TouchableOpacity 
          style={styles.trackButton} 
          onPress={(e) => {
            e.stopPropagation();
            onTrack();
          }}
        >
          <Feather name="map-pin" size={14} color="#000" />
          <Text style={styles.trackButtonText}>Track Delivery</Text>
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  );
};

export const OngoingOrderDisplay: React.FC = () => {
  const colors = useColors();
  const styles = createStyles(colors);
  const navigation = useNavigation<NavigationProp>();
  const { orders, loading, error, refetch } = useOrders();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingUrl, setTrackingUrl] = useState<string | null>(null);
  
  // Track user changes and clear cache when user switches
  // This prevents showing another user's orders after logout/login
  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id ?? null;
      
      // If user changed, clear the module-level cache
      if (userId !== cachedForUserId) {
        logger.debug('OngoingOrderDisplay', `User changed from ${cachedForUserId?.slice(0, 8) ?? 'none'} to ${userId?.slice(0, 8) ?? 'none'}, clearing cache`);
        cachedOngoingOrders = [];
        hasEverShownContent = false;
        cachedForUserId = userId;
      }
      setCurrentUserId(userId);
    };
    
    checkUser();
    
    // Also listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const userId = session?.user?.id ?? null;
      if (userId !== cachedForUserId) {
        logger.debug('OngoingOrderDisplay', `Auth state changed, user: ${userId?.slice(0, 8) ?? 'none'}, clearing cache`);
        cachedOngoingOrders = [];
        hasEverShownContent = false;
        cachedForUserId = userId;
      }
      setCurrentUserId(userId);
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);
  
  // Use module-level cache to persist across unmounts
  // This is the key fix for preventing flash during navigation
  const effectiveOrders = orders.length > 0 ? orders : cachedOngoingOrders;
  
  // Keep a ref to orders for stable comparison in polling effect
  const ordersRef = useRef(effectiveOrders);
  ordersRef.current = effectiveOrders;

  // Listen for order events to refresh data
  useEffect(() => {
    const unsubscribe = orderEvents.subscribe(() => {
      logger.debug('OngoingOrderDisplay', 'Received order update event, refreshing...');
      refetch(); // Refetch orders data - no need to force re-render, state update handles it
    });

    return unsubscribe;
  }, [refetch]);

  // Note: Removed useFocusEffect refetch - real-time subscriptions handle updates
  // and the focus refetch was causing layout flash when navigating back

  // Filter for ongoing orders using the same logic as OrderTrackingBanner
  // Use effectiveOrders to prevent flash when data briefly empties
  const ongoingOrders = useMemo(
    () => effectiveOrders.filter((order) => isOngoingOrder(order.status)),
    [effectiveOrders]
  );
  
  // Stable reference for order IDs that need polling (prevents effect restart on every render)
  const ongoingOrderIds = useMemo(
    () => ongoingOrders.map(o => o.id).join(','),
    [ongoingOrders]
  );

  // Poll order status every 10 seconds for ongoing orders with delivery_id
  useEffect(() => {
    logger.debug('OngoingOrderDisplay', `Polling effect triggered. Ongoing orders: ${ongoingOrders.length}`);

    if (ongoingOrders.length === 0) {
      return; // No ongoing orders, no need to poll
    }

    // Get orders with delivery_id that need status updates
    const ordersWithDelivery = ongoingOrders.filter(
      (order) => order.delivery_id && !["completed", "delivered", "cancelled", "rejected"].includes(order.status)
    );

    if (ordersWithDelivery.length === 0) {
      return; // No orders with delivery_id to poll
    }

    logger.debug('OngoingOrderDisplay', `Starting polling for ${ordersWithDelivery.length} orders with delivery_id`);

    const pollOrderStatuses = async () => {
      try {
        // Fetch latest order statuses from database
        const orderIds = ordersWithDelivery.map((o) => o.id);

        const { data: latestOrders, error: fetchError } = await supabase
          .from("orders")
          .select("id, status, delivery_id")
          .in("id", orderIds);

        if (fetchError) {
          logger.error('OngoingOrderDisplay', 'Error fetching latest orders', fetchError);
          return;
        }

        if (latestOrders) {
          // Check for status changes and update if needed
          let hasUpdates = false;
          for (const latestOrder of latestOrders) {
            const currentOrder = ordersRef.current.find((o) => o.id === latestOrder.id);
            const currentStatus = currentOrder?.status;
            const newStatus = latestOrder.status;

            if (currentStatus !== newStatus) {
              logger.info('OngoingOrderDisplay', `Order ${latestOrder.id.slice(0, 8)} status changed: ${currentStatus} -> ${newStatus}`);
              hasUpdates = true;
            }

            // If order has delivery_id, also check Uber API for delivered status
            if (latestOrder.delivery_id && newStatus !== "completed") {
              try {
                const { data: uberData, error: uberError } =
                  await supabase.functions.invoke("uber-delivery", {
                    body: { delivery_id: latestOrder.delivery_id },
                  });

                if (uberError) {
                  // Handle different error types gracefully
                  const errorMessage = uberError.message || String(uberError);
                  const is404 = errorMessage.includes("404") || errorMessage.includes("not found");
                  const is429 = errorMessage.includes("429") || errorMessage.includes("rate limit");
                  
                  if (!is404 && !is429) {
                    logger.warn('OngoingOrderDisplay', `Uber API error for order ${latestOrder.id.slice(0, 8)}`, errorMessage);
                  }
                  continue;
                }

                // Check if response has error field (even if no uberError)
                if (uberData?.error) {
                  logger.warn('OngoingOrderDisplay', `Uber API returned error for order ${latestOrder.id.slice(0, 8)}`, uberData.error);
                  continue;
                }

                // Map Uber status to our internal status
                const uberStatus = uberData?.uber_status?.status;
                if (uberStatus) {
                  let targetStatus: string | null = null;
                  
                  if (uberStatus === "delivered") {
                    targetStatus = "completed";
                  } else if (uberStatus === "pickup_complete") {
                    targetStatus = "in_transit";
                  } else if (uberStatus === "dropoff") {
                    targetStatus = "pickup_complete";
                  } else if (uberStatus === "pickup" || uberStatus === "shopping_completed") {
                    targetStatus = "pickup_pending";
                  }
                  
                  // Update database if Uber status indicates a change
                  if (targetStatus && newStatus !== targetStatus) {
                    logger.info('OngoingOrderDisplay', `Uber status "${uberStatus}" maps to "${targetStatus}" for order ${latestOrder.id.slice(0, 8)}, updating database...`);

                    // Update order status
                    const { error: updateError } = await supabase
                      .from("orders")
                      .update({
                        status: targetStatus,
                        updated_at: new Date().toISOString(),
                      })
                      .eq("id", latestOrder.id);

                    if (updateError) {
                      logger.error('OngoingOrderDisplay', `Error updating order to ${targetStatus}`, updateError);
                    } else {
                      logger.info('OngoingOrderDisplay', `Successfully updated order ${latestOrder.id.slice(0, 8)} to ${targetStatus}`);
                      hasUpdates = true;
                    }
                  }
                }
              } catch (err) {
                logger.warn('OngoingOrderDisplay', `Error checking Uber API for order ${latestOrder.id.slice(0, 8)}`, err);
                continue;
              }
            }
          }

          // Refetch orders if there were any updates
          if (hasUpdates) {
            logger.debug('OngoingOrderDisplay', 'Status updates detected, refetching orders...');
            refetch();
          }
        }
      } catch (err) {
        logger.error('OngoingOrderDisplay', 'Error in pollOrderStatuses', err);
      }
    };

    // Initial poll
    pollOrderStatuses();

    // Poll every 10 seconds
    const pollInterval = setInterval(pollOrderStatuses, 10000);

    return () => {
      clearInterval(pollInterval);
    };
  }, [ongoingOrderIds, refetch]);

  // Update module-level cache - always sync with current ongoing orders
  // This ensures completed orders are removed from the cache
  useEffect(() => {
    // Only update cache if we have real data from the API (not during initial load)
    // We check if orders array exists (even if empty after completion)
    if (orders.length > 0 || hasEverShownContent) {
      cachedOngoingOrders = ongoingOrders;
    }
    if (ongoingOrders.length > 0) {
      hasEverShownContent = true;
    }
  }, [ongoingOrders, orders.length]);

  // Convert Order to BuyerOrder format for navigation (same as OrderTrackingBanner)
  const convertToBuyerOrder = (order: Order): BuyerOrder => {
    return {
      id: order.id,
      created_at: order.created_at,
      status: order.status,
      delivery_status: order.delivery_status,
      delivery_id: order.delivery_id,
      total_amount: order.total_amount,
      tracking_url: order.tracking_url,
      payment_method: order.payment_method,
      payment_details: order.payment_details,
      items: order.items || [],
      delivery_fee: null,
      payment_status: null,
      payer_email: null,
      payment_intent_id: null,
    };
  };

  const handleOrderPress = (order: Order) => {
    // Use the same navigation logic as OrderTrackingBanner
    const buyerOrder = convertToBuyerOrder(order);
    (navigation as any).navigate("BuyerOrderDetails", { order: buyerOrder });
  };

  const handleTrackOrder = (order: Order) => {
    if (order.tracking_url) {
      setTrackingUrl(order.tracking_url);
      setShowTrackingModal(true);
    }
  };

  const handleViewAllOrders = () => {
    navigation.navigate("Orders");
  };

  // Use cached orders if current is empty but we've shown content before
  const displayOrders = ongoingOrders.length > 0 ? ongoingOrders : cachedOngoingOrders;
  
  // Don't show if truly no orders (including cache)
  if (displayOrders.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        {/* <Text style={styles.sectionTitle}>Ongoing Orders</Text> */}
      </View>

      <View style={styles.ordersContainer}>
        {displayOrders.slice(0, 2).map((order) => (
          <OngoingOrderCard
            key={order.id}
            order={order}
            onPress={() => handleOrderPress(order)}
            onTrack={() => handleTrackOrder(order)}
            colors={colors}
            styles={styles}
          />
        ))}

        {displayOrders.length > 2 && (
          <TouchableOpacity
            style={styles.showMoreButton}
            onPress={handleViewAllOrders}
          >
            <Text style={styles.showMoreText}>
              View {displayOrders.length - 2} more ongoing orders
            </Text>
            <Feather name="arrow-right" size={16} color={colors.primary} />
          </TouchableOpacity>
        )}
      </View>
      
      <InAppBrowserModal
        visible={showTrackingModal}
        url={trackingUrl}
        title="Delivery Tracking"
        onClose={() => setShowTrackingModal(false)}
      />
    </View>
  );
};

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      paddingHorizontal: spacing.lg,
      paddingTop: spacing.sm,
      paddingBottom: spacing.md,
      backgroundColor: colors.background,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.md,
    },
    sectionTitle: {
      fontSize: 20,
      fontWeight: "600",
      color: colors.text,
    },
    viewAllText: {
      fontSize: 14,
      color: colors.primary,
      fontWeight: "500",
    },
    ordersContainer: {
      gap: spacing.sm,
    },
    orderCard: {
      backgroundColor: colors.surface,
      borderRadius: 20,
      padding: spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.03,
      shadowRadius: 8,
      elevation: 2,
    },
    cardHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: spacing.md,
    },
    orderInfo: {
      flex: 1,
    },
    orderId: {
      fontSize: 16,
      fontWeight: "700",
      color: colors.text,
      marginBottom: 2,
    },
    orderDate: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    statusBadge: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 12,
      gap: 4,
    },
    statusText: {
      fontSize: 11,
      fontWeight: "700",
      color: "#000000",
    },
    itemsContainer: {
      marginBottom: spacing.md,
      paddingHorizontal: 2,
    },
    itemText: {
      fontSize: 14,
      color: colors.text,
      marginBottom: 4,
      fontWeight: "500",
    },
    moreItemsText: {
      fontSize: 13,
      color: colors.textSecondary,
      fontStyle: "italic",
    },
    cardFooter: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingTop: spacing.md,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    totalAmount: {
      fontSize: 18,
      fontWeight: "700",
      color: colors.text,
    },
    actionContainer: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
    },
    viewDetailsText: {
      fontSize: 14,
      color: colors.primary,
      fontWeight: "700",
    },
    loadingContainer: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      padding: spacing.lg,
      gap: spacing.sm,
    },
    loadingText: {
      fontSize: 14,
      color: colors.textLight,
    },
    errorContainer: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      padding: spacing.lg,
      gap: spacing.sm,
    },
    errorText: {
      fontSize: 14,
      color: colors.error,
    },
    retryButton: {
      paddingHorizontal: spacing.sm,
      paddingVertical: spacing.xs,
      backgroundColor: colors.error,
      borderRadius: 8,
    },
    retryText: {
      fontSize: 12,
      color: "white",
      fontWeight: "600",
    },
    showMoreButton: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      height: 52,
      backgroundColor: colors.surface,
      borderRadius: 26,
      borderWidth: 1,
      borderColor: colors.border,
      gap: spacing.sm,
      marginTop: spacing.xs,
    },
    showMoreText: {
      fontSize: 14,
      color: colors.primary,
      fontWeight: "700",
    },
    trackButton: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.primary, 
      height: 44,
      borderRadius: 22,
      marginTop: spacing.md,
      gap: 8,
      shadowColor: colors.primary,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.2,
      shadowRadius: 8,
      elevation: 4,
    },
    trackButtonText: {
      color: "#000",
      fontSize: 14,
      fontWeight: "700",
    },
  });
